import { useState, useRef, useCallback } from 'react';
import VirtualTable from '@/components/VirtualTable';
import { extractFilterOptions, filterAndSortData } from '@/components/Table/Table.functions';
import {
  Box, Text, Button, Card,
} from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { downloadFile } from '@/helpers/download';
import prepareTranslate from '@/helpers/dictionary';
import { queryStringToObject } from '@/helpers/string';
import { useParams } from 'react-router-dom';
import docsMutation from '@/api/mutitations/docs';
import PropTypes from 'prop-types';

const MonthlyPaymentTables = (props) => {
  const {
    rulesForMonthlyPayment,
    setRowsToPay,
    selectedRowsByTable,
    setSelectedRowsByTable,
    tableData,
    setTablesData,
    exportRuleData,
    exportFileTitles,
    exportFileMutate,
    generateExportFile,
  } = props;

  const getTableSum = (tableKey) => {
    const rows = selectedRowsByTable[tableKey] || [];
    return rows.reduce((sum, row) => sum + (row.amountDue || row.AgentFee || 0), 0);
  };

  // Helper function to check if table has data
  const hasTableData = (table) => (
    table && table.data && Array.isArray(table.data) && table.data.length > 0
  );

  const { query: rawUrlQuery } = useParams();
  const { mutateAsync: tableMutateAsync } = docsMutation.useExportTable();
  const [loadingStates, setLoadingStates] = useState({});

  const jsonQuery = queryStringToObject(rawUrlQuery);
  const {
    agent: agentId, year, month, company, product,
  } = jsonQuery;

  const loadedRulesRef = useRef({});
  const dataFetchingInProgress = useRef({});
  const tableDataRef = useRef(tableData);
  const t = prepareTranslate();

  // Keep ref in sync with tableData
  tableDataRef.current = tableData;

  const getTableData = useCallback(async (start, end, query, sort, ruleId, variable = null) => {
    // Create unique key for multi-variable rules
    const tableKey = variable ? `${ruleId}-${variable._id}` : ruleId;

    // Use ref to get current data without causing re-render dependency
    const currentTableData = tableDataRef.current;
    let ruleTable = currentTableData[tableKey];
    if (!ruleTable) {
      ruleTable = {};
    }
    const {
      data: ruleTableData, origionData, columnFilters, error,
    } = ruleTable;

    if (ruleTableData && loadedRulesRef.current[tableKey]) {
      const data = filterAndSortData(origionData, columnFilters, sort);
      setTablesData((prev) => ({
        ...prev,
        [tableKey]: {
          ...prev[tableKey],
          data,
          filterOptions: extractFilterOptions(data, prev[tableKey].columns),
        },
      }));

      return { data, count: data?.length, columns: ruleTable.columns };
    }

    if (error) {
      return { data: [], count: 0, columns: [] };
    }

    // Prevent duplicate fetching
    if (dataFetchingInProgress.current[tableKey]) {
      return null;
    }

    dataFetchingInProgress.current[tableKey] = true;

    const queryData = {
      year,
      month,
      identifiers: agentId,
      query: variable?.query || {},
      additionalData: {
        companyId: company,
        productId: product,
      },
      exportRuleId: ruleId,
    };

    try {
      const { data: mutateData } = await tableMutateAsync(queryData);
      const { rows, columns: dataColumns } = mutateData;

      loadedRulesRef.current[tableKey] = true;

      const defaultFilters = dataColumns
        .filter((col) => col.accessor)
        .reduce((acc, col) => {
          acc[col.accessor] = '';
          return acc;
        }, {});

      // Get previous filters from current data
      const prevFilters = tableDataRef.current[tableKey]?.columnFilters || defaultFilters;

      setTablesData((prevTableData) => ({
        ...prevTableData,
        [tableKey]: {
          columns: dataColumns,
          data: rows,
          origionData: rows,
          filterOptions: extractFilterOptions(rows, dataColumns),
          columnFilters: prevFilters,
        },
      }));

      return { data: rows, count: rows.length, columns: dataColumns };
    } catch {
      setTablesData((prev) => ({
        ...prev,
        [tableKey]: {
          ...prev[tableKey],
          error: true,
          data: [],
          columns: [],
        },
      }));

      return { data: [], count: 0, columns: [] };
    } finally {
      dataFetchingInProgress.current[tableKey] = false;
    }
  }, [setTablesData, year, month, agentId, company, product, tableMutateAsync]);

  const updateColumnFilters = (tableKey, newFilters) => {
    setTablesData((prev) => {
      const prevFilters = prev[tableKey]?.columnFilters || {};
      const resolvedFilters = newFilters(prevFilters);

      return {
        ...prev,
        [tableKey]: {
          ...prev[tableKey],
          columnFilters: resolvedFilters,
        },
      };
    });
  };

  const handleSelectionChange = (selectedRows, key) => {
    // Extract ruleId from key (could be "ruleId" or "ruleId-companyId")
    const ruleId = key.includes('-') ? key.split('-')[0] : key;

    // Update selected rows for this specific table
    setSelectedRowsByTable((prev) => ({
      ...prev,
      [key]: selectedRows || [],
    }));

    // Calculate total for ALL tables of this rule (all companies)
    const currentRule = rulesForMonthlyPayment?.find((rule) => rule._id === ruleId);
    const isNegativeAmount = currentRule?.isNegativeAmount || false;

    // Get all table keys that belong to this rule
    setSelectedRowsByTable((prev) => {
      const updatedSelections = {
        ...prev,
        [key]: selectedRows || [],
      };

      // Calculate total from all tables of this rule
      let ruleTotal = 0;
      Object.keys(updatedSelections).forEach((tableKey) => {
        // Check if this table belongs to the current rule
        const tableRuleId = tableKey.includes('-') ? tableKey.split('-')[0] : tableKey;
        if (tableRuleId === ruleId) {
          const tableRows = updatedSelections[tableKey] || [];
          const tableSum = tableRows.reduce(
            (sum, row) => sum + (row.amountDue || row.AgentFee || 0),
            0,
          );
          ruleTotal += tableSum;
        }
      });

      // Update the total for this rule
      setRowsToPay((prevTotals) => ({
        ...prevTotals,
        [ruleId]: isNegativeAmount ? -ruleTotal : ruleTotal,
      }));

      return updatedSelections;
    });
  };

  const handleShowClick = async (tableKey, ruleId, variable = null) => {
    setLoadingStates((prev) => ({ ...prev, [tableKey]: true }));
    try {
      // Check if we already have data for this table
      const existingTable = tableData[tableKey];

      // Prepare data and columns
      let data;

      if (existingTable?.data && existingTable?.columns) {
        // Use existing data if available
        data = existingTable.data;
      } else {
        // Otherwise fetch new data
        const result = await getTableData(0, 0, {}, {}, ruleId, variable);
        data = result.data;
      }

      const variableName = variable?.name || null;

      const generateExportFileObj = {
        ruleId,
        tableKey,
        variableName,
        exportRuleData,
        exportFileTitles,
        tableData,
        exportFileMutate,
        rows: data,
        columns: variable?.visiblecolumns,
        visiblecolumnsTransforms: variable?.visiblecolumnsTransforms,
      };

      const { fileData, fileTitle } = await generateExportFile(generateExportFileObj);

      downloadFile(fileData, fileTitle);
    } catch (error) {
      // Silently handle the error - TO DO: add proper error handling
    } finally {
      setLoadingStates((prev) => ({ ...prev, [tableKey]: false }));
    }
  };

  return (
    <>
      {rulesForMonthlyPayment?.map((rule, index) => {
        const { _id: ruleId, title, variables } = rule;

        // Check if this rule has multiple variables
        const hasMultipleVariables = variables && Array.isArray(variables) && variables.length > 0;

        if (hasMultipleVariables) {
          // Render multiple tables - one for each variable
          return (
            < >
              {index !== 0 && <Text fw={'bold'} mb={'sm'}>{title}</Text>}

              {variables.map((variable) => {
                const { _id: variableId, name: variableName } = variable;
                const tableKey = `${ruleId}-${variableId}`;
                const table = tableData[tableKey];

                if (!table) return null;

                const { columns: tableColumns, filterOptions, columnFilters } = table;

                // Create a function for data fetching with variable parameters
                const getDataForTable = (start, end, query, sort) => (
                  getTableData(start, end, query, sort, ruleId, variable)
                );

                return (
                  <>
                    {hasTableData(table) && <Text fw={'bold'}>{variableName}</Text>}

                    <>
                      <VirtualTable
                        columns={tableColumns?.filter((col) => col.isVisible)}
                        getData={getDataForTable}
                        filterOptions={filterOptions}
                        columnFilters={columnFilters}
                        setColumnFilters={(newFilters) => updateColumnFilters(tableKey, newFilters)}
                        showSelectionColumn={true}
                        onSelectRow={(rows) => handleSelectionChange(rows, tableKey)}
                        showEmptyState={false}
                        height='60vh'
                        ready={table.data !== undefined || !!table.error}
                      />
                    </>

                    {(selectedRowsByTable[tableKey] || []).length > 0 && (
                      <Card
                        p='0.4rem 1rem'
                        withBorder
                        bg='var(--mantine-color-gray-0)'
                        style={{ width: 'fit-content' }}
                        mb='sm'
                      >
                        <Text fw={600} size='sm' style={{ whiteSpace: 'nowrap' }}>
                          {t('pages.monthlyPayment.sum', [getTableSum(tableKey).toLocaleString()])}
                        </Text>
                      </Card>
                    )}

                    {index === 0 && hasTableData(table)
                      && <Button
                        mb='md'
                        leftSection={<IconEye />}
                        loading={loadingStates[tableKey] || false}
                        onClick={() => handleShowClick(tableKey, ruleId, variable)}>
                        {t('pages.monthlyPayment.moreInformation')}
                      </Button>}
                  </>
                );
              })}
            </>
          );
        }

        // Original single table logic for rules without multiple companies
        const table = tableData[ruleId];
        if (!table) return null;
        const { columns: tableColumns, filterOptions, columnFilters } = table;

        // Create a function for data fetching
        const getDataForTable = (start, end, query, sort) => (
          getTableData(start, end, query, sort, ruleId)
        );

        return (
          <div key={ruleId}>
            {index !== 0 && hasTableData(table) && <Text fw={'bold'} mb={'sm'}>{title}</Text>}

            <Box>
              <VirtualTable
                columns={tableColumns?.filter((col) => col.isVisible)}
                getData={getDataForTable}
                filterOptions={filterOptions}
                columnFilters={columnFilters}
                setColumnFilters={(newFilters) => updateColumnFilters(ruleId, newFilters)}
                showSelectionColumn={true}
                onSelectRow={(rows) => handleSelectionChange(rows, ruleId)}
                showEmptyState={false}
                height='60vh'
                ready={table.data !== undefined || !!table.error}
              />
            </Box>

            {(selectedRowsByTable[ruleId] || []).length > 0 && (
              <Card
                p='0.4rem 1rem'
                withBorder
                bg='var(--mantine-color-gray-0)'
                style={{ width: 'fit-content' }}
                mb='sm'
              >
                <Text fw={600} size='sm' style={{ whiteSpace: 'nowrap' }}>
                  {t('pages.monthlyPayment.sum', [getTableSum(ruleId).toLocaleString()])}
                </Text>
              </Card>
            )}

            {index === 0 && hasTableData(table)
              && <Button
                mb='md'
                leftSection={<IconEye />}
                loading={loadingStates[ruleId] || false}
                onClick={() => handleShowClick(ruleId, ruleId)}>
                {t('pages.monthlyPayment.moreInformation')}
              </Button>}
          </div>
        );
      })}
    </>
  );
};

MonthlyPaymentTables.propTypes = {
  rulesForMonthlyPayment: PropTypes.array,
  setRowsToPay: PropTypes.func,
  selectedRowsByTable: PropTypes.object,
  setSelectedRowsByTable: PropTypes.func,
  tableData: PropTypes.object,
  setTablesData: PropTypes.func,
  exportRuleData: PropTypes.object,
  exportFileTitles: PropTypes.object,
  exportFileMutate: PropTypes.func,
  generateExportFile: PropTypes.func,
};
export default MonthlyPaymentTables;
